import React from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  return (
    <div>
      <Helmet>
        <title>Privacy Policy - Zorvian Associates | Your Privacy Matters</title>
        <meta name="description" content="Read Zorvian Associates' privacy policy to understand how we collect, use, and protect your personal information." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] py-24 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-heading">
              Privacy Policy
            </h1>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            
            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 font-heading">Introduction</h2>
            <p className="text-slate-600 mb-6">
              At Zorvian Associates, we are committed to protecting your privacy and ensuring the security of your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our 
              website or use our legal services.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Information We Collect</h2>
            <h3 className="text-2xl font-bold text-[#0F172A] mb-3 mt-8">Personal Information</h3>
            <p className="text-slate-600 mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Request a consultation or legal services</li>
              <li>Contact us via phone, email, or contact forms</li>
              <li>Subscribe to our newsletters or communications</li>
              <li>Create an account on our website</li>
            </ul>
            <p className="text-slate-600 mb-6">
              This information may include your name, email address, phone number, address, and details about your legal matter.
            </p>

            <h3 className="text-2xl font-bold text-[#0F172A] mb-3 mt-8">Automatically Collected Information</h3>
            <p className="text-slate-600 mb-6">
              When you visit our website, we automatically collect certain information about your device, including information 
              about your web browser, IP address, time zone, and cookies. We use this information to improve our website and 
              better understand how visitors use our services.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Provide, maintain, and improve our legal services</li>
              <li>Respond to your inquiries and consultation requests</li>
              <li>Send you information about our services, updates, and legal insights</li>
              <li>Process transactions and manage client relationships</li>
              <li>Comply with legal obligations and maintain professional standards</li>
              <li>Detect, prevent, and address technical issues or security concerns</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Information Sharing and Disclosure</h2>
            <p className="text-slate-600 mb-6">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in 
              the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li><strong>With your consent:</strong> We may share your information when you have given us explicit permission</li>
              <li><strong>Legal obligations:</strong> When required by law, court order, or legal process</li>
              <li><strong>Service providers:</strong> With trusted third-party service providers who assist in operating our website 
                  and conducting our business, under strict confidentiality agreements</li>
              <li><strong>Professional consultants:</strong> With other legal professionals or experts when necessary to provide 
                  you with comprehensive legal services</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Attorney-Client Privilege</h2>
            <p className="text-slate-600 mb-6">
              Please note that communications through this website do not automatically create an attorney-client relationship. 
              Once an attorney-client relationship is established, all communications are protected by attorney-client privilege 
              and held in the strictest confidence in accordance with professional ethics rules.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Data Security</h2>
            <p className="text-slate-600 mb-6">
              We implement appropriate technical and organizational security measures to protect your personal information against 
              unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet 
              or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Your Rights</h2>
            <p className="text-slate-600 mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Access and receive a copy of your personal information</li>
              <li>Request correction of inaccurate or incomplete information</li>
              <li>Request deletion of your personal information (subject to legal and ethical obligations)</li>
              <li>Opt-out of marketing communications</li>
              <li>Object to certain processing of your information</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Cookies and Tracking Technologies</h2>
            <p className="text-slate-600 mb-6">
              We use cookies and similar tracking technologies to track activity on our website and hold certain information. 
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you 
              do not accept cookies, you may not be able to use some portions of our website.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Children's Privacy</h2>
            <p className="text-slate-600 mb-6">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information 
              from children. If you become aware that a child has provided us with personal information, please contact us.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Changes to This Privacy Policy</h2>
            <p className="text-slate-600 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy 
              Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically 
              for any changes.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg mb-6">
              <p className="text-slate-700 mb-2"><strong>Email:</strong> Info@zorvianassociates.com</p>
              <p className="text-slate-700 mb-2"><strong>Phone:</strong> +1 (844) 921-2228</p>
              <p className="text-slate-700"><strong>Address:</strong> 166 Geary St Ste 1500 #1787, San Francisco, CA 94108, USA</p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
